import { PageContainer } from "@/components/layout/PageContainer";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { ArrowRight, Users, TrendingUp, Heart, Zap } from "lucide-react";

const WorkforceCustomerExperience = () => {
  return (
    <PageContainer>
      <Helmet>
        <title>Workforce & Customer Experience Solutions - Scienza Health</title>
        <meta name="description" content="AI-powered workforce optimization and customer experience solutions that enhance operational efficiency and patient satisfaction." />
      </Helmet>
      
      <div className="min-h-screen mobile-section">
        <div className="mobile-container">
          <div className="text-center mobile-hero">
            <h1 className="mobile-text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
              Workforce & Customer Experience
            </h1>
            <p className="mobile-text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
              Optimize your healthcare workforce and enhance patient experience through intelligent automation and AI-driven insights.
            </p>
          </div>

          {/* Key Benefits */}
          <div className="mobile-grid mobile-grid-4 gap-4 sm:gap-6 mb-12 sm:mb-16">
            <div className="text-center mobile-card">
              <div className="bg-primary/10 rounded-full w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4 touch-target">
                <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
              </div>
              <h3 className="mobile-text-base sm:text-lg font-semibold mb-2">Productivity Boost</h3>
              <p className="mobile-text-sm text-muted-foreground">Increase staff efficiency by 40%</p>
            </div>
            <div className="text-center mobile-card">
              <div className="bg-primary/10 rounded-full w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4 touch-target">
                <Heart className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
              </div>
              <h3 className="mobile-text-base sm:text-lg font-semibold mb-2">Patient Satisfaction</h3>
              <p className="mobile-text-sm text-muted-foreground">Improve HCAHPS scores</p>
            </div>
            <div className="text-center mobile-card">
              <div className="bg-primary/10 rounded-full w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4 touch-target">
                <Zap className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
              </div>
              <h3 className="mobile-text-base sm:text-lg font-semibold mb-2">Faster Response</h3>
              <p className="mobile-text-sm text-muted-foreground">Reduce response times by 70%</p>
            </div>
            <div className="text-center mobile-card">
              <div className="bg-primary/10 rounded-full w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto mb-4 touch-target">
                <Users className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
              </div>
              <h3 className="mobile-text-base sm:text-lg font-semibold mb-2">Staff Retention</h3>
              <p className="mobile-text-sm text-muted-foreground">Reduce burnout and turnover</p>
            </div>
          </div>

          {/* Solutions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-12 md:mb-16">
            <Link to="/solutions/workforce-customer-experience/workforce-optimization" className="group touch-target">
              <div className="mobile-card bg-card border hover:shadow-lg transition-all duration-300 h-full">
                <div className="flex items-center mb-4">
                  <Users className="h-6 w-6 sm:h-8 sm:w-8 text-primary mr-3" />
                  <h3 className="mobile-text-lg sm:text-xl md:text-2xl font-semibold group-hover:text-primary transition-colors">
                    Workforce Optimization
                  </h3>
                </div>
                <p className="mobile-text-sm sm:text-base text-muted-foreground mb-6">
                  Intelligent workforce management that optimizes staff scheduling, automates routine tasks, 
                  and provides data-driven insights to improve operational efficiency and reduce burnout.
                </p>
                <ul className="space-y-2 mobile-text-sm text-muted-foreground mb-6">
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Smart scheduling and shift optimization
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Automated task assignment and prioritization
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Real-time workload balancing
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Predictive staffing analytics
                  </li>
                </ul>
                <div className="flex items-center text-primary touch-target">
                  <span className="mobile-text-sm sm:text-base font-medium">Learn More</span>
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>

            <Link to="/solutions/workforce-customer-experience/customer-experience" className="group touch-target">
              <div className="mobile-card bg-card border hover:shadow-lg transition-all duration-300 h-full">
                <div className="flex items-center mb-4">
                  <Heart className="h-6 w-6 sm:h-8 sm:w-8 text-primary mr-3" />
                  <h3 className="mobile-text-lg sm:text-xl md:text-2xl font-semibold group-hover:text-primary transition-colors">
                    Customer Experience
                  </h3>
                </div>
                <p className="mobile-text-sm sm:text-base text-muted-foreground mb-6">
                  Enhanced patient experience through personalized interactions, proactive communication, 
                  and seamless care coordination that improves satisfaction and outcomes.
                </p>
                <ul className="space-y-2 mobile-text-sm text-muted-foreground mb-6">
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Personalized patient communication
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Proactive care reminders and follow-ups
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Real-time feedback collection and analysis
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    Seamless omnichannel experience
                  </li>
                </ul>
                <div className="flex items-center text-primary touch-target">
                  <span className="mobile-text-sm sm:text-base font-medium">Learn More</span>
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>
          </div>

          {/* Impact Metrics */}
          <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg mobile-px-safe py-6 sm:p-8 mb-12 md:mb-16">
            <h2 className="mobile-text-xl sm:text-2xl font-bold text-center mb-6 sm:mb-8">Proven Impact</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
              <div className="text-center">
                <div className="mobile-text-2xl sm:text-3xl font-bold text-primary mb-2">40%</div>
                <p className="mobile-text-sm sm:text-base text-muted-foreground">Increase in staff productivity</p>
              </div>
              <div className="text-center">
                <div className="mobile-text-2xl sm:text-3xl font-bold text-primary mb-2">25%</div>
                <p className="mobile-text-sm sm:text-base text-muted-foreground">Reduction in staff turnover</p>
              </div>
              <div className="text-center">
                <div className="mobile-text-2xl sm:text-3xl font-bold text-primary mb-2">95%</div>
                <p className="mobile-text-sm sm:text-base text-muted-foreground">Patient satisfaction rate</p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center bg-muted rounded-lg mobile-px-safe py-6 sm:p-8">
            <h2 className="mobile-text-xl sm:text-2xl md:text-3xl font-bold mb-4">Transform Your Workforce and Patient Experience</h2>
            <p className="mobile-text-base sm:text-lg text-muted-foreground mb-6 sm:mb-8 max-w-2xl mx-auto">
              Discover how our solutions can optimize your workforce efficiency while enhancing patient satisfaction and care quality.
            </p>
            <Link 
              to="/contact-request-demo" 
              className="mobile-btn-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium rounded-lg"
            >
              Schedule a Demo <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default WorkforceCustomerExperience;